import { loadTemplates } from 'components/form_builder/features/data/dataSlice'
import axios from 'lib/axios'
import { getPathsFromConditions, getPathsFromTemplate } from 'lib/utilities/form/utilities'
import { mapValues } from 'lodash'
import { useCallback, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { load as loadBase } from '../../features/base/baseSlice'
import { load as loadComponents, setSelected } from '../../features/components/componentSlice'
import { load as loadFieldDefs } from '../../features/fields/fieldsSlice'
import { load as loadPages, setFormName, setPageWidth } from '../../features/pages/pageSlice'
import { uniqueComponentKey } from '../utility'

const SIGNATURE_LABEL = {
  signature: 'signature',
  name: 'full name',
  time: 'date signed',
}

const POSITIONS = ['x', 'y', 'width', 'height', 'page']

const isPercent = (x) => String(x).slice(-1) == '%'
const removePercentage = (x) => parseFloat(isPercent(x) ? String(x).slice(0, -1) : x)
const removePercentagesFromPosition = (pos) =>
  Object.fromEntries(POSITIONS.map((attr) => [attr, removePercentage(pos[attr])]))

function componentFromFieldPosition({ id, position, label, placeholder, checkbox, edit_on_page }) {
  const type = checkbox || edit_on_page ? 'checkbox' : 'data'
  return {
    id,
    position: removePercentagesFromPosition(position),
    type: type,
    label: label || 'Field',
    placeholder: placeholder || 'Placeholder',
  }
}

function componentFromSignature({ id, positions }) {
  return Object.entries(positions).map(([subtype, position]) => ({
    id,
    subtype,
    position: removePercentagesFromPosition(position),
    type: 'signature',
    label: SIGNATURE_LABEL[subtype],
    placeholder: SIGNATURE_LABEL[subtype],
  }))
}

function updateComponents({ dispatch, field_positions = [], signatures = [] }) {
  const data_field_positions = Object.fromEntries(
    field_positions.filter((e) => !e.checkbox && !e.edit_on_page).map((position) => [position.id, position])
  )
  const checkbox_field_positions = Object.fromEntries(
    field_positions.filter((e) => e.checkbox || e.edit_on_page).map((position) => [position.id, position])
  )
  const components = []
    .concat(Object.values(field_positions).map(componentFromFieldPosition))
    .concat(Object.values(signatures).map(componentFromSignature).flat())

  const dataTemplates = mapValues(data_field_positions, (field_position) => {
    field_position.position = uniqueComponentKey('data', field_position.id)
    const templatePaths = getPathsFromTemplate(field_position.template)
    field_position.templatePaths = templatePaths
    field_position.paths = field_position.fields.map((path) => {
      const type = templatePaths.includes(path) ? 'templatePath' : 'fillPath'
      return { type, path }
    })
    return field_position
  })

  const checkboxTemplates = mapValues(checkbox_field_positions, (field_position) => {
    field_position.position = uniqueComponentKey('checkbox', field_position.id)
    const templatePaths = getPathsFromConditions(field_position.checkbox?.conditional_logic)
    field_position.templatePaths = templatePaths
    field_position.paths = field_position.fields?.map((path) => {
      const type = templatePaths.includes(path) ? 'templatePath' : 'fillPath'
      return { type, path }
    })
    if (!field_position.checkbox && field_position.edit_on_page) field_position.checkbox = { symbol: 'check' }
    return field_position
  })
  const signatureTemplates = mapValues(signatures, (signature) => {
    Object.keys(signature.positions).forEach((key) => {
      signature.positions[key] = uniqueComponentKey('signature', signature.id, key)
    })

    return signature
  })

  dispatch(loadComponents(components))
  dispatch(loadTemplates({ type: 'data', templates: dataTemplates }))
  dispatch(loadTemplates({ type: 'checkbox', templates: checkboxTemplates }))
  dispatch(loadTemplates({ type: 'signature', templates: signatureTemplates }))
}

export default function useLoaderFormData() {
  const dispatch = useDispatch()
  const [isInitialDataFetched, setIsInitialDataFetched] = useState(false)
  const [loading, setLoading] = useState(false)

  const proccessData = useCallback(async (selectedKeys) => {
    try {
      const response = await axios.get('form_builder/data', { params: { width: 1400 } })
      const { form, base } = response.data
      const pages = { pages: form.pages, page_files: form.page_files }

      dispatch(setPageWidth(form.page_width))
      dispatch(loadPages(pages))
      dispatch(loadFieldDefs({ field_defs: form.field_defs, html_nodes: form.html_nodes }))
      dispatch(setFormName(form.name))
      dispatch(loadBase({ roles: base.roles, formId: form.id, metadata: base.metadata }))
      updateComponents({
        dispatch,
        field_positions: form?.field_positions,
        signatures: form?.signatures,
      })
      dispatch(setSelected(selectedKeys || []))
    } catch (error) {
      console.log(error)
    }
  }, [])

  const fetchInitialData = useCallback(async () => {
    try {
      setLoading(true)
      setIsInitialDataFetched(false)
      await proccessData()
      setLoading(false)
      setIsInitialDataFetched(true)
    } catch (error) {
      console.log(error)
    }
  }, [proccessData])

  const refeshData = useCallback(async ({ selectedKeys }) => {
    try {
      setLoading(true)
      await proccessData(selectedKeys)
      setLoading(false)
    } catch (error) {
      console.log(error)
    }
  }, [proccessData])

  useEffect(() => {
    fetchInitialData()
  }, [fetchInitialData])

  return { isInitialDataFetched, loading, fetchInitialData, refeshData }
}
